import { Link, useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Badge,
  V2Card,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import type { SalesInvoice, SalesInvoiceStatus } from '../../lib/types/crm.js';
import { useApiGet } from '../../lib/useApiGet.js';

const STATUS_LABEL: Record<SalesInvoiceStatus, string> = {
  draft: 'پیش‌نویس',
  submitted: 'ارسال‌شده برای تأیید',
  approved: 'تأییدشده',
  posted: 'ثبت قطعی',
  reversed: 'برگشت‌خورده',
};

const STATUS_TONE: Record<SalesInvoiceStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'info',
  posted: 'success',
  reversed: 'danger',
};

/** Read-only projection of the immutable sales-invoice snapshot and its live AR balance. */
export function SalesInvoiceDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, invoiceId } = useParams<{ slug: string; invoiceId: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const invoice = useApiGet<SalesInvoice>(
    slug && invoiceId ? `${base}/api/v1/sales/invoices/${invoiceId}` : null,
    [slug, invoiceId],
  );

  if (!slug || !invoiceId) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {invoice.status === 'loading' ? <LoadingState /> : null}
      {invoice.status === 'error' ? (
        <StatePanel
          variant={
            invoice.error.status === 403
              ? 'permission_denied'
              : invoice.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            invoice.error.status === 403
              ? 'دسترسی مشاهدهٔ فاکتور را ندارید'
              : invoice.error.status === 404
                ? 'فاکتور پیدا نشد'
                : 'فاکتور قابل نمایش نیست'
          }
        />
      ) : null}

      {invoice.status === 'ready' ? (
        <>
          <div className="sk-breadcrumb">
            <Link to={`${base}/crm/orders/${invoice.data.orderRef}`}>
              سفارش {invoice.data.orderRef}
            </Link>
            <span>/</span>
            <b className="ltr">{invoice.data.officialNumber ?? invoice.data.invoiceId}</b>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)' }}>فاکتور فروش</h1>
            <V2Badge tone={STATUS_TONE[invoice.data.status]}>
              {STATUS_LABEL[invoice.data.status]}
            </V2Badge>
          </div>

          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
              }}
            >
              <div>
                <small>طرف‌حساب</small>
                <div>
                  <PartyLink base={base} partyRef={invoice.data.partyRef}>
                    {invoice.data.partyName} ({invoice.data.partyRef})
                  </PartyLink>
                </div>
              </div>
              <div>
                <small>تاریخ فاکتور</small>
                <div>{displayJalaliDate(invoice.data.businessDate)}</div>
              </div>
              <div>
                <small>سررسید ثبت‌شده</small>
                <div>{displayJalaliDate(invoice.data.dueDate)}</div>
              </div>
              <div>
                <small>شرایط پرداخت</small>
                <div>
                  {invoice.data.paymentTerm.name} (
                  {invoice.data.paymentTerm.dueDays.toLocaleString('fa-IR')} روز)
                </div>
              </div>
              <div>
                <small>مبلغ کل</small>
                <div className="sk-num">
                  {formatRialFa(invoice.data.totalAmountRial, unit)} {label}
                </div>
              </div>
              <div>
                <small>ماندهٔ حساب دریافتنی</small>
                <div className="sk-num">
                  {formatRialFa(invoice.data.openBalanceRial, unit)} {label}
                </div>
                <Link to={`${base}/finance/ar/${invoice.data.arOpenItemId}`}>
                  {invoice.data.receivableStatus}
                </Link>
              </div>
              <div>
                <small>سند درآمد و مالیات</small>
                <div>
                  <Link to={`${base}/finance/journals/${invoice.data.revenueJournalId}`}>
                    مشاهده سند
                  </Link>
                </div>
              </div>
            </div>
          </V2Card>

          <V2Card>
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>ردیف / کالا</th>
                    <th>مقدار</th>
                    <th>قیمت واحد پیش از مالیات</th>
                    <th>تخفیف</th>
                    <th>خالص</th>
                    <th>مالیات</th>
                    <th>جمع</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.data.lines.map((line) => (
                    <tr key={line.invoiceLineId}>
                      <td>
                        {line.rowNumber.toLocaleString('fa-IR')} — {line.description}
                        <div className="ltr">{line.productRef}</div>
                      </td>
                      <td className="sk-num">
                        {line.quantity} {line.uomRef}
                      </td>
                      <td className="sk-num">{formatRialFa(line.unitPriceRial, unit)}</td>
                      <td className="sk-num">{formatRialFa(line.discountAmountRial, unit)}</td>
                      <td className="sk-num">{formatRialFa(line.taxableBaseRial, unit)}</td>
                      <td className="sk-num">{formatRialFa(line.vatAmountRial, unit)}</td>
                      <td className="sk-num">{formatRialFa(line.totalAmountRial, unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </V2Card>

          <V2Card>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--sk-divider)' }}>
              <strong>شواهد حواله‌های خروج</strong>
            </div>
            <div style={{ padding: 18, display: 'grid', gap: 8 }}>
              {invoice.data.lines.flatMap((line) =>
                line.allocations.map((allocation) => (
                  <div key={`${line.invoiceLineId}-${allocation.movementId}`}>
                    ردیف {line.rowNumber.toLocaleString('fa-IR')}، مقدار {allocation.quantity}{' '}
                    <Link to={`${base}/inventory/movements/${allocation.movementId}`}>
                      مشاهده حواله خروج
                    </Link>
                  </div>
                )),
              )}
            </div>
          </V2Card>

          <V2Card>
            <div style={{ padding: 18 }}>
              <strong>Snapshot مالیات:</strong>{' '}
              {invoice.data.taxSnapshot
                ? `${invoice.data.taxSnapshot.name}، نسخه ${invoice.data.taxSnapshot.version.toLocaleString('fa-IR')}، مبلغ ${formatRialFa(invoice.data.vatAmountRial, unit)} ${label}`
                : 'بدون مالیات'}
            </div>
          </V2Card>
        </>
      ) : null}
    </div>
  );
}
